// useEffect is used for the diagnostic console.log below.
import { useEffect, useState } from "react";
import type { AccountInfo } from "@azure/msal-browser";
import { getActiveAccount, signIn as msalSignIn, signOut as msalSignOut } from "./auth.js";
import { ADMIN_CONFIG } from "./config.js";

interface UseAuth {
  account: AccountInfo | null;
  loading: boolean;
  error: string | null;
  hasAdminRole: boolean;
  roles: string[];
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * Pull the Entra App Role claim from an ID token's parsed claims. MSAL writes
 * `idTokenClaims` as a loose object; the `roles` claim is a string array when
 * any app roles are assigned to this user for this app, and absent otherwise.
 */
function rolesFromAccount(account: AccountInfo | null): string[] {
  if (!account?.idTokenClaims) return [];
  const claims = account.idTokenClaims as { roles?: unknown };
  if (!Array.isArray(claims.roles)) return [];
  return claims.roles.filter((r): r is string => typeof r === "string");
}

export function useAuth(): UseAuth {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setAccount(await getActiveAccount());
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signIn = async () => {
    setError(null);
    try {
      await msalSignIn();
      // No setAccount here — msalSignIn redirects the page; when we come back,
      // the boot-time getActiveAccount() call inside the effect above picks
      // up the new account.
    } catch (e) {
      setError((e as Error).message);
      throw e;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await msalSignOut();
      setAccount(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const roles = rolesFromAccount(account);
  const hasAdminRole = roles.includes(ADMIN_CONFIG.adminRole);

  // Diagnostic logging — leaves a trail in the browser console so we can
  // verify what's actually in the token without having to decode it at jwt.io.
  // Logs once per sign-in (when account changes), not every render.
  useEffect(() => {
    if (!account) return;
     
    console.log("[admin/auth] signed in as", account.username, {
      roles,
      hasAdminRole,
      expectedRole: ADMIN_CONFIG.adminRole,
      tokenClaims: account.idTokenClaims,
    });
  }, [account, roles, hasAdminRole]);

  return { account, loading, error, hasAdminRole, roles, signIn, signOut };
}
